import React from "react";
import { Droppable } from "@hello-pangea/dnd";
import { Task, TaskStatus, Project } from "../../types/Work";
import { SortableTaskCard } from "./SortableTaskCard";

interface KanbanColumnProps {
  id: TaskStatus;
  title: string;
  tasks: Task[];
  projects?: Project[];
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  onToggleTimer: (task: Task) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onViewAll?: () => void;
  totalTaskCount?: number;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  id,
  title,
  tasks,
  projects,
  onEditTask,
  onDeleteTask,
  onToggleTimer,
  onStatusChange,
  onViewAll,
  totalTaskCount,
}) => {
  const getStatusIcon = () => {
    switch (id) {
      case "backlog":
        return "bi-inbox";
      case "todo":
        return "bi-list-check";
      case "in_progress":
        return "bi-arrow-clockwise";
      case "in_review":
        return "bi-eye";
      case "done":
        return "bi-check-circle";
      default:
        return "bi-inbox";
    }
  };

  const getStatusColor = () => {
    switch (id) {
      case "backlog":
        return "#8e8e93";
      case "todo":
        return "#007aff";
      case "in_progress":
        return "#ff9500";
      case "in_review":
        return "#af52de";
      case "done":
        return "#34c759";
      default:
        return "#8e8e93";
    }
  };

  const getTotalEstimation = () => {
    if (id !== "todo" && id !== "in_progress") return null;

    const totalMinutes = tasks.reduce((total, task) => {
      return total + (task.estimationMinutes || 0);
    }, 0);

    if (totalMinutes === 0) return null;

    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    }
    if (hours > 0) {
      return `${hours}h`;
    }
    return `${mins}m`;
  };

  const totalEstimation = getTotalEstimation();

  return (
    <div className={`kanban-column ` + id}>
      <div className="column-header">
        <div className="column-title-group">
          <i
            className={`bi ${getStatusIcon()}`}
            style={{ color: getStatusColor() }}
          ></i>
          <h3>{title}</h3>
        </div>
        <div className="column-header-right">
          {totalEstimation && (
            <span className="column-estimation">
              <i className="fas fa-stopwatch"></i>
              {totalEstimation}
            </span>
          )}
          <span
            className="task-count"
            style={{
              backgroundColor: `${getStatusColor()}20`,
              color: getStatusColor(),
            }}
          >
            {totalTaskCount ?? tasks.length}
          </span>
        </div>
      </div>
      <Droppable droppableId={id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`column-tasks ${
              snapshot.isDraggingOver ? "is-dragging-over" : ""
            }`}
          >
            {tasks.length === 0 ? (
              <div className="empty-column">
                <i className={`bi ${getStatusIcon()}`}></i>
                <p>No tasks</p>
              </div>
            ) : (
              tasks.map((task, index) => (
                <SortableTaskCard
                  key={task.id}
                  task={task}
                  index={index}
                  projects={projects}
                  onEdit={onEditTask}
                  onDelete={onDeleteTask}
                  onToggleTimer={onToggleTimer}
                  onStatusChange={onStatusChange}
                />
              ))
            )}
            {provided.placeholder}
            {onViewAll && (totalTaskCount ?? 0) > 0 && (
              <button type="button" className="view-all-done" onClick={onViewAll}>
                <i className="fas fa-clock-rotate-left"></i>
                View all history ({totalTaskCount})
              </button>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
};
